import type { AppNotification } from '../components/Preview/api';
import {
  buildActivityDeepLinkFromNotification,
  isActivityTimelineNotification,
} from './activityDeepLink';
import {
  isChatRequestNotification,
  isMeetingNotification,
  isProfileAccessNotification,
  isQuestionNotification,
  resolveNotificationNavigationTarget,
  rewriteNotificationPath,
} from './notificationActions';

export type NotificationFilter = 'all' | 'messages' | 'activity' | 'opportunities';

export type NotificationKind = 'like' | 'message' | 'view' | 'feedback' | 'general';

export type NotificationCardModel = {
  id: string;
  notification: AppNotification;
  kind: NotificationKind;
  title: string;
  subtitle?: string;
  quote?: string;
  timeLabel: string;
  actionLabel: string | null;
  isRead: boolean;
};

export function notificationKindFromMessage(message: string): NotificationKind {
  const msg = message.toLowerCase();
  if (msg.includes('liked your prompt') || msg.includes('liked your profile')) {
    return 'like';
  }
  if (
    msg.includes('message') ||
    msg.includes('question') ||
    msg.includes('asked') ||
    msg.includes('chat')
  ) {
    return 'message';
  }
  if (msg.includes('viewed') || msg.includes('profile was viewed')) {
    return 'view';
  }
  if (msg.includes('feedback') || msg.includes('rated')) {
    return 'feedback';
  }
  return 'general';
}

export function matchesNotificationFilter(
  kind: NotificationKind,
  filter: NotificationFilter
): boolean {
  if (filter === 'all') return true;
  if (filter === 'messages') return kind === 'message';
  if (filter === 'activity') {
    return kind === 'like' || kind === 'view' || kind === 'general';
  }
  if (filter === 'opportunities') {
    return kind === 'feedback' || kind === 'message';
  }
  return true;
}

export function formatNotificationTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const diffMs = now.getTime() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins >= 0 && mins < 60 && date >= startOfToday) {
    if (mins < 1) return 'Just now';
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }

  if (date >= startOfToday) {
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (date >= startOfYesterday && date < startOfToday) {
    return `Yesterday, ${date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function groupNotificationsByDay(
  items: NotificationCardModel[]
): { label: string; items: NotificationCardModel[] }[] {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const today: NotificationCardModel[] = [];
  const yesterday: NotificationCardModel[] = [];
  const older: NotificationCardModel[] = [];

  for (const item of items) {
    const date = new Date(item.notification.createdAt);
    if (Number.isNaN(date.getTime())) {
      older.push(item);
      continue;
    }
    if (date >= startOfToday) {
      today.push(item);
    } else if (date >= startOfYesterday) {
      yesterday.push(item);
    } else {
      older.push(item);
    }
  }

  const groups: { label: string; items: NotificationCardModel[] }[] = [];
  if (today.length) groups.push({ label: 'Today', items: today });
  if (yesterday.length) groups.push({ label: 'Yesterday', items: yesterday });
  if (older.length) groups.push({ label: 'Earlier', items: older });
  return groups;
}

function parseLikedPrompt(message: string) {
  const match = message.match(/^(.*?)\s+liked your prompt:\s*"([^"]+)"\.?$/i);
  if (match) {
    return {
      recruiter: match[1]?.trim() || 'Someone',
      prompt: match[2]?.trim() || '',
    };
  }
  const loose = message.match(/^(.*?)\s+liked your prompt/i);
  return {
    recruiter: loose?.[1]?.trim() || 'Someone',
    prompt: '',
  };
}

function parseQuestion(message: string) {
  const asked = message.match(/^(.*?)\s+asked a new question about your (.+?)\.?$/i);
  if (asked) {
    return {
      title: `${asked[1].trim()} asked a new question about your ${asked[2].trim()}`,
      quote: '',
    };
  }
  const quoteMatch = message.match(/'([^']+)'/);
  return {
    title: message.split(/'[^']*'/)[0]?.trim() || message,
    quote: quoteMatch?.[1] || '',
  };
}

export function buildNotificationCard(notification: AppNotification): NotificationCardModel {
  const message = notification.message || '';
  const kind = notificationKindFromMessage(message);
  const timeLabel = formatNotificationTime(notification.createdAt);

  if (kind === 'like') {
    const liked = parseLikedPrompt(message);
    return {
      id: notification.id,
      notification,
      kind,
      title: `${liked.recruiter} liked your prompt`,
      subtitle: liked.prompt ? `Prompt: '${liked.prompt}'` : undefined,
      timeLabel,
      actionLabel: 'View Prompt',
      isRead: notification.isRead,
    };
  }

  if (isProfileAccessNotification(notification)) {
    return {
      id: notification.id,
      notification,
      kind: 'general',
      title: message,
      timeLabel,
      actionLabel: 'Review request',
      isRead: notification.isRead,
    };
  }

  if (isChatRequestNotification(notification)) {
    return {
      id: notification.id,
      notification,
      kind: 'message',
      title: message,
      timeLabel,
      actionLabel: 'Review request',
      isRead: notification.isRead,
    };
  }

  if (kind === 'message') {
    const q = parseQuestion(message);
    return {
      id: notification.id,
      notification,
      kind,
      title: q.title,
      quote: q.quote || undefined,
      timeLabel,
      actionLabel: isQuestionNotification(notification) ? 'Open Questions' : 'Reply',
      isRead: notification.isRead,
    };
  }

  if (kind === 'view') {
    return {
      id: notification.id,
      notification,
      kind,
      title: message,
      subtitle: 'Keep it up! Your profile is getting attention.',
      timeLabel,
      actionLabel: null,
      isRead: notification.isRead,
    };
  }

  if (kind === 'feedback') {
    return {
      id: notification.id,
      notification,
      kind,
      title: message,
      subtitle: 'A recruiter rated your prompt',
      timeLabel,
      actionLabel: null,
      isRead: notification.isRead,
    };
  }

  return {
    id: notification.id,
    notification,
    kind,
    title: message,
    timeLabel,
    actionLabel: 'Open',
    isRead: notification.isRead,
  };
}

export function resolveNotificationPath(notification: AppNotification): string {
  if (notification.action_url?.startsWith('/activity')) {
    return rewriteNotificationPath(notification.action_url);
  }
  if (isActivityTimelineNotification(notification)) {
    return buildActivityDeepLinkFromNotification(notification);
  }
  return resolveNotificationNavigationTarget(notification);
}

export function resolveNotificationAction(
  notification: AppNotification
): { label: string; href: string } | null {
  const href = resolveNotificationPath(notification);

  if (isProfileAccessNotification(notification)) {
    return { label: 'Review access request', href };
  }
  if (isQuestionNotification(notification)) {
    return { label: 'Open Questions', href };
  }
  if (isChatRequestNotification(notification)) {
    return { label: 'Review chat request', href };
  }
  if (isMeetingNotification(notification)) {
    return { label: 'Open Calendar', href: '/calendar' };
  }
  if (href.startsWith('/activity')) {
    return { label: 'Open Activity', href };
  }
  const msg = String(notification.message || '').toLowerCase();
  if (msg.includes('liked your prompt')) {
    return { label: 'View Prompt', href: '/prompts' };
  }
  if (msg.includes('liked your profile')) {
    return { label: 'Open Profile', href: '/user-profile' };
  }
  if (msg.includes('reference')) {
    return { label: 'Open References', href: '/dashboard/references' };
  }
  if (msg.includes('question') || msg.includes('message') || msg.includes('ask')) {
    return { label: 'Open Messages', href: '/messages' };
  }
  return { label: 'Open', href };
}
